import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { ArrowLeft, Globe, Users, Clock, CheckCircle, AlertCircle, Briefcase, DollarSign } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CampaignDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [matchScore, setMatchScore] = useState(null);
  const [funding, setFunding] = useState(false);

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  useEffect(() => {
    if (user && campaign) fetchMatchScore();
  }, [user, campaign]);

  const fetchCampaign = async () => {
    try {
      const { data } = await axios.get(`${API}/campaigns/${id}`);
      setCampaign(data);
      // Check if already applied
      if (user) {
        try {
          const { data: apps } = await axios.get(`${API}/applications/my`, { headers: getAuthHeaders() });
          if (apps.some(a => a.campaign_id === id)) setApplied(true);
        } catch {}
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchScore = async () => {
    try {
      const { data } = await axios.get(`${API}/campaigns/${id}/match-score`, { headers: getAuthHeaders() });
      setMatchScore(data);
    } catch {}
  };

  const handleApply = async () => {
    if (!user) { navigate('/login'); return; }
    setApplying(true);
    try {
      await axios.post(`${API}/applications`, { campaign_id: id }, { headers: getAuthHeaders() });
      setApplied(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const handleFundCampaign = async () => {
    setFunding(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(`${API}/payments/checkout/session`, {
        campaign_id: id,
        origin_url: originUrl
      }, { headers: getAuthHeaders() });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setFunding(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!campaign) return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
      <p className="text-[#9CA3AF]">Campaign not found.</p>
    </div>
  );

  const c = campaign;

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="campaign-detail">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-6 sm:py-8">
        {/* Back button */}
        <button onClick={() => navigate('/campaigns')} data-testid="back-to-campaigns" className="flex items-center gap-2 text-[#9CA3AF] hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Campaigns
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div className="bg-[#111827] border border-white/10 p-4 sm:p-8 border-l-[3px] border-l-[#FF4343]">
              <div className="flex items-center gap-3 sm:gap-4 mb-4">
                {c.brand?.logo_url && <img src={c.brand.logo_url} alt="" className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-sm bg-white/10" />}
                <div>
                  <span className="text-[#9CA3AF] text-sm">{c.brand_name}</span>
                  {c.brand?.verified && <span className="ml-1 text-[#FF4343]">&#10003; Verified</span>}
                </div>
              </div>
              <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold mb-2" data-testid="campaign-title">{c.title}</h1>
              <div className="flex flex-wrap gap-3 mt-4">
                <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-white/5 text-[#9CA3AF] border border-white/10">{c.industry}</span>
                {c.platforms?.map(p => (
                  <span key={p} className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-white/5 text-[#9CA3AF] border border-white/10">{p}</span>
                ))}
                <span className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#10B981]">
                  <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> {c.status}
                </span>
              </div>
            </div>

            {/* Campaign Brief */}
            <div className="bg-[#111827] border border-white/10 p-4 sm:p-8">
              <h2 className="font-display text-xl font-semibold mb-4">Campaign Brief</h2>
              <p className="text-[#9CA3AF] leading-relaxed mb-6">{c.description}</p>

              {c.talking_points?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" /> Talking Points
                  </h3>
                  <ul className="space-y-2">
                    {c.talking_points.map((tp, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                        <span className="text-[#FF4343] mt-1">-</span> {tp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {c.guardrails?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-400" /> Guardrails
                  </h3>
                  <ul className="space-y-2">
                    {c.guardrails.map((g, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                        <span className="text-red-400 mt-1">-</span> {g}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {c.example_post && (
                <div className="bg-[#090E33] border border-white/10 p-4 mt-4">
                  <h3 className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2">Example Post</h3>
                  <p className="text-sm text-[#9CA3AF] italic leading-relaxed">{c.example_post}</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Apply Card */}
            <div className="bg-[#111827] border border-white/10 p-4 sm:p-6 lg:sticky lg:top-24 order-first lg:order-none">
              {matchScore && (
                <div className="mb-4 p-3 bg-[#FF4343]/5 border border-[#FF4343]/20" data-testid="match-score">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-mono uppercase tracking-wider text-[#6B7280]">Match Score</span>
                    <span className="text-2xl font-mono font-bold text-[#FF4343]">{matchScore.score}%</span>
                  </div>
                  <p className="text-xs text-[#9CA3AF]">{matchScore.reasoning}</p>
                </div>
              )}

              <h3 className="font-display text-lg font-semibold mb-4">Compensation</h3>
              <div className="space-y-3 mb-6">
                {c.flat_fee && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CA3AF]">Flat Fee</span>
                    <span className="font-mono text-[#FF4343]">${c.flat_fee.toLocaleString()}</span>
                  </div>
                )}
                {c.cpm_rate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CA3AF]">CPM Rate</span>
                    <span className="font-mono text-[#FF4343]">${c.cpm_rate}</span>
                  </div>
                )}
                {c.floor_guarantee && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CA3AF]">Floor Guarantee</span>
                    <span className="font-mono text-[#FF4343]">${c.floor_guarantee.toLocaleString()}</span>
                  </div>
                )}
                {c.performance_bonus_amount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CA3AF]">Bonus (at {c.performance_bonus_threshold?.toLocaleString()} impressions)</span>
                    <span className="font-mono text-[#FF4343]">+${c.performance_bonus_amount}</span>
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6 pt-4 border-t border-white/10">
                <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                  <Users className="w-4 h-4" /> <span>Required: {c.exec_tier_required}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                  <Briefcase className="w-4 h-4" /> <span>Min {c.min_followers?.toLocaleString()} followers</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                  <Clock className="w-4 h-4" /> <span>Deadline: {c.deadline ? new Date(c.deadline).toLocaleDateString() : 'Open'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                  <DollarSign className="w-4 h-4" /> <span>Paid within 7 days of verified post</span>
                </div>
              </div>

              {/* Spots */}
              <div className="mb-6">
                <div className="flex justify-between text-xs text-[#6B7280] mb-1">
                  <span>{c.spots_filled} of {c.spots_total} spots filled</span>
                </div>
                <div className="w-full h-2 bg-white/5 overflow-hidden">
                  <div className="h-full bg-[#FF4343]" style={{ width: `${(c.spots_filled / c.spots_total) * 100}%` }} />
                </div>
              </div>

              {applied ? (
                <div className="w-full py-3 text-center bg-[#10B981]/10 border border-[#10B981]/20 text-[#10B981] text-sm font-medium" data-testid="applied-badge">
                  Applied Successfully
                </div>
              ) : (
                <button
                  onClick={handleApply} disabled={applying}
                  data-testid="apply-campaign-btn"
                  className="w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] py-3 text-sm font-semibold transition-all duration-300 disabled:opacity-50"
                >
                  {applying ? 'Applying...' : 'Apply to This Campaign'}
                </button>
              )}

              {/* Fund Campaign for Brands */}
              {user?.role === 'admin' && (
                <button
                  onClick={handleFundCampaign}
                  disabled={funding}
                  data-testid="fund-campaign-btn"
                  className="w-full mt-3 border border-white/20 bg-transparent text-white hover:bg-white/5 py-3 text-sm font-medium transition-all duration-200 disabled:opacity-50"
                >
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  {funding ? 'Processing...' : 'Fund This Campaign'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
